/*globals VXConfig*/

'use strict';

import FluxEventEmitter from '../FluxEventEmitter';
import assign from 'object-assign';
import ActionCreator from '../actions/ActionCreator';
import Flux from '../Flux';
import Dispatcher from '../Dispatcher';
import Constants from '../Constants';
import EnumGA4Events from '../../utils/EnumGA4Events';
import { trackPurchase } from '../../utils/DataLayerHelper';
import { doVxqlRequest } from './VxqlStore/common.js';

const ActionTypes = Constants.ActionTypes;

const ChangeEvents = {
	show: 'show',
	hide: 'hide',
	chatExit: 'chatExit',
	buyTicket: 'buyTicket',
	privateTicketShowInvitation: 'privateTicketShowInvitation',
};

let _isChatExitOpen = false;

function setIsChatExitOpen(isOpen) {
	_isChatExitOpen = isOpen;

	MessengerStore.emitChatExitEvent(isOpen);
}

function buyChatTicket(ticket, creationType = 'C') {
	if (ticket) {
		Flux.Vxql.buyChatTicket(ticket, creationType, (result) => {
			if (result.success) {
				const { show, ticket } = result;

				trackPurchase({
					[EnumGA4Events.DEFAULT_PARAMETER_TRANSACTION_ID]: ticket.id + ':' + show.id, // required, pseudo transaction id
					[EnumGA4Events.DEFAULT_PARAMETER_CURRENCY]: show.price.currency,
					[EnumGA4Events.DEFAULT_PARAMETER_VALUE]: show.price.value,
					[EnumGA4Events.DEFAULT_PARAMETER_ITEMS]: [
						{
							[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_ID]: ticket.id,
							[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_BRAND]: show.actorName,
							[EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_NAME]:
								EnumGA4Events.DEFAULT_PARAMETER_ITEMS__ITEM_CATEGORY__VALUE_CAM_SHOW_TICKET,
						},
					],
				});
			}

			MessengerStore.emitBuyTicketEvent(result);
		});
	}
}

function removeChatTicket(ticket) {
	if (ticket) {
		Flux.Vxql.removeChatTicket(ticket, (result) => {
			MessengerStore.emitBuyTicketEvent(result);
		});
	}
}

// Messenger-related VXQL functions migrated from VXQLStore

/**
 * Get unread messages count
 * @param {Function} callback
 */
function getUnreadMessagesCount(callback) {
	const query = `
	query{
		guest {
		    messages {
		      unreadCount
		    }
		}
	}`;

	doVxqlRequest(query).then(callback);
}

/**
 * Get messenger gifts
 * @param {Function} callback
 */
function getMessengerGifts(callback) {
	const query = `
	query {
		messenger {
			gifts {
				id
				name
				price {currency value symbol}
				imageUrl
				active
				vip
				categoryId
				translations {language name value}
			}
		}
	}`;

	doVxqlRequest(query).then(callback);
}

/**
 * Get messenger toy controls
 * @param {Function} callback
 */
function getMessengerToyControls(callback) {
	const query = `
	query {
		messenger {
			toyControls {
				id
				name
				price {currency value symbol}
				imageUrl
				active
				vip
				categoryId
				translations {language name value}
				duration
				displayName
				random
			}
		}
	}`;

	doVxqlRequest(query).then(callback);
}

/**
 * Get messenger content
 * @param {number} first
 * @param {number} offset
 * @param {Object} filter
 * @param {string} order
 * @param {Function} callback
 */
function getMessengerContent(first = 10, offset = 0, filter = null, order = '', callback) {
	const args = { first, offset, filter, order };
	const query = `
	query messengerContent($first: Int!, $offset: Int!, $filter: MessengerContentFilterInput!, $order: String) {
		guest {
		  id
		  messengerContent(first: $first, offset: $offset, filter: $filter, order: $order) {
			items {
			  messageId
			  text
			  date
			  model {
				name
				linkVX(internal: true)
				id
                avatar {
                    url(size: w60)
                }
			  }
			  media {
				type
				origSrc
				src
				srcSet
				width
				height
				duration
				previewPicture
			  }
			}
			total
		  }
		}
	  }`;

	doVxqlRequest(query, args).then(callback);
}

/**
 * Get channels
 * @param {Function} callback
 * @param {boolean} blocked
 */
function getChannels(callback, blocked = null) {
	const query = `
query ($first: Int, $offset: Int, $filter: MessengerChannelsFilterInput) {
  guest {
    messengerChannels(first: $first, offset: $offset, filter: $filter) {
      items {
        channelId
        actorId
        guestId
        unreadCount
        blocked
        model {
          name
        }
      }
      total
    }
  }
}
	`;

	const args = {
		first: 500,
		offset: 0,
		filter: blocked !== null ? { blocked } : null,
	};

	doVxqlRequest(query, args).then(callback);
}

/**
 * Start anonymous voice call chat
 * @param {Function} callback
 * @param {number} actorId
 * @param {string} accessToken
 */
function startAnonymousVoicecallChatQuery(callback, actorId, accessToken) {
	const args = { actorId: actorId, accessToken: accessToken };
	const query = `
	mutation($accessToken: String!, $actorId: Int!) {
		messenger {
			startAnonymousVoicecallChat(accessToken: $accessToken, actorId: $actorId) {
				resultCode
				server
				clientId
			}
		}
    }`;
	doVxqlRequest(query, args).then((result) => callback(result));
}

/**
 * Touch channel
 * @param {number} actorId
 */
function touchChannelQuery(actorId) {
	const query = `
mutation ($actorId: Int!) {
  guest {
    touchMessengerChannel(actorId: $actorId)
    }
  }
`;
	return doVxqlRequest(query, { actorId: actorId });
}

const MessengerStore = assign({}, FluxEventEmitter.prototype, {
	startChat: function (chatType, actorId) {
		const config = {
			chatType,
			actorId,
			mode: Flux.Constants.VXMessenger.Modes.WEBTOKEN,
		};

		// select channel to update sidebar
		ActionCreator.Messenger.selectChannelByActorId(actorId, true, false, false);

		this.emit(ChangeEvents.show, config);
	},
	startAnonymousVoicecallChat: function (actorId) {
		const onCreate = (result) => {
			const serverInfo = result.data.messenger.startAnonymousVoicecallChat;
			if (serverInfo && serverInfo.resultCode === 2000) {
				const config = {
					chatType: Flux.Constants.VXMessenger.ChatTypes.LIVECHAT,
					actorId: actorId,
					mode: Flux.Constants.VXMessenger.Modes.SERVER,
					serverInfo: { server: serverInfo.server, clientId: serverInfo.clientId },
				};

				this.emit(ChangeEvents.show, config);
			} else if (serverInfo.resultCode) {
				this.emit(ChangeEvents.show, {
					errorCode: serverInfo.resultCode,
					actorId: actorId,
				});
			}
		};

		startAnonymousVoicecallChatQuery(onCreate, actorId, VXConfig.accessToken);
	},
	touchChannel: function (actorId) {
		touchChannelQuery(actorId);
	},
	isChatExitOpen: function () {
		return _isChatExitOpen;
	},
	addStartChatListener: function (callback) {
		this.on(ChangeEvents.show, callback);
	},
	removeStartChatListener: function (callback) {
		this.removeListener(ChangeEvents.show, callback);
	},

	stopChat: function () {
		this.emit(ChangeEvents.hide, false);
	},
	addStopChatListener: function (callback) {
		this.on(ChangeEvents.hide, callback);
	},
	removeStopChatListener: function (callback) {
		this.removeListener(ChangeEvents.hide, callback);
	},
	addChatExitChangeListener: function (callback) {
		this.on(ChangeEvents.chatExit, callback);
	},
	removeChatExitChangeListener: function (callback) {
		this.removeListener(ChangeEvents.chatExit, callback);
	},
	emitChatExitEvent: function (isOpen) {
		this.emit(ChangeEvents.chatExit, isOpen);
	},
	addChatTicketChangeListener: function (callback) {
		this.on(ChangeEvents.buyTicket, callback);
	},
	removeChatTicketChangeListener: function (callback) {
		this.removeListener(ChangeEvents.buyTicket, callback);
	},
	emitBuyTicketEvent: function (result) {
		this.emit(ChangeEvents.buyTicket, result);
	},
	addPrivateTicketShowInvitationListener: function (callback) {
		this.on(ChangeEvents.privateTicketShowInvitation, callback);
	},
	removePrivateTicketShowInvitationListener: function (callback) {
		this.removeListener(ChangeEvents.privateTicketShowInvitation, callback);
	},
	handlePrivateTicketShowInvitation: function (actorName, actorProfilePictureUrl, actorId) {
		this.emit(ChangeEvents.privateTicketShowInvitation, { actorName, actorProfilePictureUrl, actorId });
	},

	// VXQL methods migrated from VXQLStore
	getUnreadMessagesCount: function (callback) {
		getUnreadMessagesCount(callback);
	},
	getMessengerGifts: function (callback) {
		getMessengerGifts(callback);
	},
	getMessengerToyControls: function (callback) {
		getMessengerToyControls(callback);
	},
	getMessengerContent: function (first, offset, filter, order, callback) {
		getMessengerContent(first, offset, filter, order, callback);
	},
	getChannels: function (callback, blocked) {
		getChannels(callback, blocked);
	},
});

MessengerStore.dispatchToken = Dispatcher.register(function (action) {
	switch (action.type) {
		case ActionTypes.Messenger.SET_IS_CHAT_EXIT_OPEN:
			setIsChatExitOpen(action.isOpen);
			break;
		case ActionTypes.Messenger.BUY_CHAT_TICKET:
			buyChatTicket(action.ticket, action.creationType);
			break;
		case ActionTypes.Messenger.REMOVE_CHAT_TICKET:
			removeChatTicket(action.ticket);
			break;
		default:
			break;
	}
});

export default MessengerStore;
